/** Lee solo el directorio central del ZIP (hasta 2 GB) con `Blob.slice`. Fuera de alcance a propósito: zip64,
 * ZIP anidados y nombres partidos por `\` (el backend recorta por `/`, `bateria_dispatch._basename`). */

const FIRMA_EOCD = 0x06054b50
const FIRMA_CENTRAL = 0x02014b50
const EOCD_MINIMO = 22
const CABECERA_CENTRAL = 46
/** Bit 11 del flag de propósito general: el nombre viene en UTF-8. Sin él, es cp437. */
const BANDERA_UTF8 = 0x800
const CENTINELA_16 = 0xffff
const CENTINELA_32 = 0xffffffff
/** El comentario del ZIP mide 0xFFFF como máximo, así que el EOCD nunca arranca más atrás. */
const COLA_MAXIMA = EOCD_MINIMO + CENTINELA_16

/** cp437 de 0x80 a 0xFF: lo que manda el formato con el bit 11 en cero (ZIP hechos en Windows). */
const CP437_ALTO =
  'ÇüéâäàåçêëèïîìÄÅÉæÆôöòûùÿÖÜ¢£¥₧ƒáíóúñÑªº¿⌐¬½¼¡«»░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■\u00a0'

/** Los mismos formatos que el backend sabe convertir en página (`bateria_ingest`). */
const SUFIJOS_ESCANEO = ['.pdf', '.png', '.jpg', '.jpeg', '.tif', '.tiff', '.bmp', '.gif', '.webp']

function decodificarNombre(bytes: Uint8Array, bandera: number): string {
  if (bandera & BANDERA_UTF8) return new TextDecoder().decode(bytes)
  let texto = ''
  for (const byte of bytes) texto += byte < 0x80 ? String.fromCharCode(byte) : CP437_ALTO[byte - 0x80]
  return texto
}

/** El largo del comentario tiene que cerrar exacto contra el final de la cola: así se descarta una
 * firma que aparezca por casualidad dentro de datos comprimidos. */
export function ubicarEocd(cola: Uint8Array): {posicion: number; tamano: number} | null {
  if (cola.length < EOCD_MINIMO) return null
  const vista = new DataView(cola.buffer, cola.byteOffset, cola.byteLength)
  for (let i = cola.length - EOCD_MINIMO; i >= 0; i--) {
    if (vista.getUint32(i, true) !== FIRMA_EOCD) continue
    if (i + EOCD_MINIMO + vista.getUint16(i + 20, true) !== cola.length) continue
    const entradas = vista.getUint16(i + 10, true)
    const tamano = vista.getUint32(i + 12, true)
    const desplazamiento = vista.getUint32(i + 16, true)
    if (entradas === CENTINELA_16 || tamano === CENTINELA_32 || desplazamiento === CENTINELA_32) return null
    return {posicion: i, tamano}
  }
  return null
}

/** Un directorio truncado entrega lo que alcanzó a leer, nunca un nombre a medias. */
export function nombresDelDirectorio(directorio: Uint8Array): string[] {
  const vista = new DataView(directorio.buffer, directorio.byteOffset, directorio.byteLength)
  const nombres: string[] = []
  let pos = 0
  while (pos + CABECERA_CENTRAL <= directorio.length) {
    if (vista.getUint32(pos, true) !== FIRMA_CENTRAL) break
    const bandera = vista.getUint16(pos + 8, true)
    const largoNombre = vista.getUint16(pos + 28, true)
    const largoExtra = vista.getUint16(pos + 30, true)
    const largoComentario = vista.getUint16(pos + 32, true)
    const inicio = pos + CABECERA_CENTRAL
    if (inicio + largoNombre > directorio.length) break
    nombres.push(decodificarNombre(directorio.subarray(inicio, inicio + largoNombre), bandera))
    pos = inicio + largoNombre + largoExtra + largoComentario
  }
  return nombres
}

export function esNombreDeEscaneo(nombre: string): boolean {
  const bajo = nombre.toLowerCase()
  return SUFIJOS_ESCANEO.some((sufijo) => bajo.endsWith(sufijo))
}

/** Recortados a la última componente, contra la que el backend compara el manifiesto. Ante lo que no
 * sepa leer devuelve lista vacía: es una pista, no un requisito. */
export async function leerNombresDeEscaneos(archivo: Blob): Promise<string[]> {
  const largoCola = Math.min(archivo.size, COLA_MAXIMA)
  const cola = new Uint8Array(await archivo.slice(archivo.size - largoCola).arrayBuffer())
  const eocd = ubicarEocd(cola)
  if (!eocd) return []
  // Se retrocede desde el EOCD y no por el desplazamiento declarado: un ZIP con bytes antepuestos
  // (autoextraíble, correo) se sigue leyendo porque el tamaño es relativo y el desplazamiento no.
  const fin = archivo.size - largoCola + eocd.posicion
  const inicio = fin - eocd.tamano
  if (inicio < 0) return []
  const directorio = new Uint8Array(await archivo.slice(inicio, fin).arrayBuffer())
  const nombres = new Set<string>()
  for (const ruta of nombresDelDirectorio(directorio)) {
    const base = ruta.slice(ruta.lastIndexOf('/') + 1)
    // `._algo.pdf` son los AppleDouble de macOS: no son la hoja de nadie.
    if (!base || base.startsWith('._') || !esNombreDeEscaneo(base)) continue
    nombres.add(base)
  }
  return [...nombres]
}
